import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { proposalsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { inr } from '../mock';
import { PlusCircle, Search, FileText, Eye, Loader2, Box, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const statusColor = {
  Draft: 'bg-slate-100 text-slate-700',
  Sent: 'bg-sky-100 text-sky-800',
  Won: 'bg-emerald-100 text-emerald-800',
  Lost: 'bg-rose-100 text-rose-700',
  Negotiation: 'bg-amber-100 text-amber-800',
};

export default function Proposals() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'Director' || user.role === 'Admin');
  const [props, setProps] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);

  const removeProposal = async () => {
    if (!confirmDel) return;
    try {
      await proposalsApi.remove(confirmDel.id);
      setProps(prev => prev.filter(x => x.id !== confirmDel.id));
      toast.success(`Proposal ${confirmDel.id} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setConfirmDel(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await proposalsApi.list();
        setProps(data);
      } catch (e) {
        toast.error('Failed to load proposals');
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => props.filter(p =>
    q === '' || (p.customer || '').toLowerCase().includes(q.toLowerCase()) || p.id.toLowerCase().includes(q.toLowerCase())
  ), [props, q]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950">Proposals</h1>
          <p className="text-slate-600 mt-1">{filtered.length} proposal{filtered.length !== 1 ? 's' : ''} generated.</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => nav('/app/proposals/new')}>
          <PlusCircle className="w-4 h-4 mr-2" /> New Proposal
        </Button>
      </div>

      <Card className="p-4 border-emerald-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by customer or proposal ID…" className="pl-10" />
        </div>
      </Card>

      <Card className="border-emerald-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50/60">
              <tr className="text-left text-emerald-900">
                <th className="px-5 py-3 font-semibold">Proposal ID</th>
                <th className="px-5 py-3 font-semibold">Customer</th>
                <th className="px-5 py-3 font-semibold">Size</th>
                <th className="px-5 py-3 font-semibold">Net Cost</th>
                <th className="px-5 py-3 font-semibold">Payback</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-5 py-16 text-center text-slate-500">
                  <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  No proposals yet. Click “New Proposal” to create one.
                </td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} className="border-t border-emerald-50 hover:bg-emerald-50/30 cursor-pointer" onClick={() => nav('/app/proposals/' + p.id)}>
                  <td className="px-5 py-3 font-mono text-emerald-800 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      {p.id}
                      {p.design3d && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                          <Box className="w-3 h-3" />3D
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-900">{p.customer}</td>
                  <td className="px-5 py-3 text-slate-700">{p.kw} kW</td>
                  <td className="px-5 py-3 text-slate-700">{inr(p.netCost)}</td>
                  <td className="px-5 py-3 text-slate-700">{p.payback} yrs</td>
                  <td className="px-5 py-3 text-slate-500">{p.date}</td>
                  <td className="px-5 py-3"><Badge className={`${statusColor[p.status] || 'bg-slate-100'} hover:bg-inherit border-0 rounded-full text-[10px]`}>{p.status}</Badge></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" className="text-emerald-700" onClick={(e) => { e.stopPropagation(); nav('/app/proposals/' + p.id); }}><Eye className="w-4 h-4 mr-1" /> View</Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={(e) => { e.stopPropagation(); setConfirmDel(p); }} data-testid={`del-proposal-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete proposal {confirmDel?.id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the proposal for <strong>{confirmDel?.customer}</strong>. The customer's shared link will stop working.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeProposal} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
